import React, { useState, useEffect } from "react";
import IconButton from "@material-ui/core/IconButton";
import ThumbUpIconFilled from "@material-ui/icons/ThumbUpAlt";
import ThumbUpIconOutlined from "@material-ui/icons/ThumbUpOutlined";
import { useTranslation } from "react-i18next";
import SignInDialog from "../signin/SigninDialog";
import { useAuth } from "../../firebase/AuthProvider";
import Tooltip from "../shared/Tooltip";
import {
  CommentLike,
  likeOrUnlikeComment,
  getLikes,
} from "../../firebase/commentlikes";
import { makeStyles } from "@material-ui/core/styles";
import { colors } from "../../theme";

interface Props {
  commentId: string;
  authorUserId: string;
}

const useStyles = makeStyles((theme) => ({
  commentLiked: {
    color: colors.LIKED,
    padding: "4px"
  },
  padding: {
    padding: "4px"
  },
}));

export default function Likes({ commentId, authorUserId }: Props) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { isSignedIn, user } = useAuth();
  const initialLikes = {
    comment: { commentId, userId: authorUserId },
    likedBy: [],
  };
  const [likes, setLikes] = useState<CommentLike>(initialLikes);
  const [openSignInDialog, setOpenSignInDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const liked = likes?.likedBy?.find((u) => u.userId === user?.id)
    ? true
    : false;

  useEffect(() => {
    async function fetch() {
      const likes = await getLikes(commentId);
      setLikes(likes ? likes : initialLikes);
    }
    fetch();
  }, [commentId, initialLikes]);

  async function handleLikeUnlikeClick(didLike: boolean) {
    if (!isSignedIn || !user) {
      setOpenSignInDialog(true);
      return;
    }

    setIsLoading(true);

    if (didLike) {
      setLikes((commentLike: CommentLike) => {
        return {
          ...commentLike,
          likedBy: [
            ...commentLike.likedBy,
            { userId: user.id, displayname: user.displayname },
          ],
        };
      });
    } else {
      setLikes((commentLike: CommentLike) => {
        return {
          ...commentLike,
          likedBy: commentLike.likedBy.filter((u) => u.userId !== user.id),
        };
      });
    }

    await likeOrUnlikeComment(commentId, authorUserId, user, didLike);

    const likes = await getLikes(commentId);
    setLikes(likes ? likes : initialLikes);

    setIsLoading(false);
  }

  const handleClose = () => {
    setOpenSignInDialog(false);
  };

  return (
    <>
      <SignInDialog open={openSignInDialog} onClose={handleClose} />
      {liked ? (
        <Tooltip title={t("comment.unliketooltip")}>
          <IconButton
            className={classes.commentLiked}
            onClick={async () => await handleLikeUnlikeClick(false)}
            disabled={isLoading}
            aria-label={t("comment.unliketooltip")}
          >
            <ThumbUpIconFilled />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title={t("comment.liketooltip")}>
          <IconButton
            color="default"
            className={classes.padding}
            onClick={async () => await handleLikeUnlikeClick(true)}
            disabled={isLoading}
            aria-label={t("comment.liketooltip")}
          >
            <ThumbUpIconOutlined />
          </IconButton>
        </Tooltip>
      )}
      {likes.likedBy.length} {t("comment.likes")} <br />
    </>
  );
}
